"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface DailyRow {
  date: string;
  impressions: number;
  reach: number;
  engagement: number;
  postCount?: number;
}

export interface PlatformRow {
  platform: string;
  impressions: number;
  engagement: number;
  postCount: number;
  sharePct: number;
}

type Metric = "impressions" | "reach" | "engagement";
type Mode = "line" | "stacked";

interface DailyTrendChartProps {
  daily: DailyRow[];
  byPlatform: PlatformRow[];
}

// NOTE: The spec asks for a stacked-by-platform area chart, but the overview
// API returns `byPlatform` as period totals (not per-day). Until the server
// exposes a per-day platform breakdown, the "stacked" mode renders a filled
// area of the same metric totals from `daily[]`. The mode toggle is wired up
// so the upgrade is a drop-in data-shape change.
export function DailyTrendChart({ daily, byPlatform: _byPlatform }: DailyTrendChartProps) {
  const { t } = useTranslation("analytics");
  const [mode, setMode] = useState<Mode>("line");
  const [metric, setMetric] = useState<Metric>("impressions");

  const data = daily.map((d) => ({
    date: d.date,
    value: d[metric],
  }));

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4 flex items-center justify-between gap-2">
        <Select value={metric} onValueChange={(v) => setMetric(v as Metric)}>
          <SelectTrigger className="h-8 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="impressions">{t("metric.impressions")}</SelectItem>
            <SelectItem value="reach">{t("metric.reach")}</SelectItem>
            <SelectItem value="engagement">{t("metric.engagement")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          <Button
            variant={mode === "line" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("line")}
          >
            {t("chart.line")}
          </Button>
          <Button
            variant={mode === "stacked" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("stacked")}
          >
            {t("chart.stacked")}
          </Button>
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {mode === "line" ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          ) : (
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary) / 0.25)"
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
      {mode === "stacked" && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("chart.comingSoon")}
        </p>
      )}
    </div>
  );
}
